"use client";

import {
  startTransition,
  useActionState,
  useMemo,
  useRef,
  useState,
} from "react";
import dynamic from "next/dynamic";
import Link from "next/link";
import { Check } from "lucide-react";
import type { DateRange } from "react-day-picker";

import {
  submitTripBooking,
  type TripBookingState,
} from "@/lib/actions/tripBooking";
import { getRecaptchaToken } from "@/lib/recaptcha";
import { Button } from "@/components/ui/Button";
import { Input, Label, Select, Textarea } from "@/components/ui/Fields";
import { cn } from "@/lib/format";
import countryCodeData from "@/lib/country-code.json";
import { Stepper } from "../ui/Stepper";

// Lazy-load the calendar (react-day-picker + date-fns, ~90KB) — kept out of the
// initial bundle; the grid streams in when the form renders.
const DayPicker = dynamic(
  () => import("@/components/home/LazyDayPicker").then((m) => m.DayPicker),
  {
    ssr: false,
    loading: () => (
      <div className="custom-date-picker grid min-h-[18rem] place-items-center text-sm text-muted-soft">
        Loading calendar…
      </div>
    ),
  },
);

type PackageOption = { id: number; title: string };

type Props = {
  /** Every bookable package, `{ id, title }`, for the package selector. */
  packages: PackageOption[];
  className?: string;
};

type GtagFn = (...args: unknown[]) => void;
function fireGtag(event: string, params: Record<string, string | number>) {
  if (typeof window !== "undefined") {
    (window as unknown as { gtag?: GtagFn }).gtag?.("event", event, params);
  }
}

function startOfToday(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), d.getDate());
}

/** `YYYY-MM-DD` in local time. */
function toISO(date: Date | undefined): string {
  if (!date) return "";
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, "0");
  const d = String(date.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
}

function fmt(date: Date | undefined): string {
  return date
    ? new Intl.DateTimeFormat("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      }).format(date)
    : "—";
}

function nights(range: DateRange | undefined): number | null {
  if (!range?.from || !range.to) return null;
  return Math.round((+range.to - +range.from) / 86_400_000);
}

/** Numbered section header — gives the form a clear, scannable hierarchy. */
function StepHeading({
  step,
  title,
  hint,
}: {
  step: number;
  title: string;
  hint?: string;
}) {
  return (
    <div className="mb-5 flex items-baseline gap-3">
      <span
        className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary text-sm font-semibold text-white"
        aria-hidden
      >
        {step}
      </span>
      <div>
        <h3 className="uppercase font-extrabold text-base text-headings">
          {title}
        </h3>
        {hint ? <p className="mt-0.5 text-xs text-muted-soft">{hint}</p> : null}
      </div>
    </div>
  );
}

export function TripBookingForm({ packages, className }: Props) {
  const [ts] = useState(() => String(Date.now()));
  const [pageUrl] = useState(() =>
    typeof window !== "undefined" ? window.location.href : "",
  );
  const [awaitingCaptcha, setAwaitingCaptcha] = useState(false);
  const formRef = useRef<HTMLFormElement>(null);
  const today = useMemo(() => startOfToday(), []);

  const [packageId, setPackageId] = useState("");
  const [range, setRange] = useState<DateRange | undefined>(undefined);
  const [travelers, setTravelers] = useState(1);
  const [terms, setTerms] = useState(false);

  const [state, action, pending] = useActionState<
    TripBookingState | null,
    FormData
  >(submitTripBooking, null);

  const isCustom = packageId === "0";
  const selectedPackage = useMemo(
    () => packages.find((p) => String(p.id) === packageId),
    [packages, packageId],
  );

  const countryNameOptions = useMemo(
    () => countryCodeData.map((c) => ({ label: c.name, value: c.name })),
    [],
  );
  const dialCodeOptions = useMemo(
    () =>
      countryCodeData.map((c) => ({
        key: c.name,
        label: `${c.name} (${c.dial_code})`,
        value: c.dial_code,
      })),
    [],
  );

  const serverErr =
    state && !state.ok && state.code === "validation"
      ? (state.fields ?? {})
      : {};
  const tripNights = nights(range);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!e.currentTarget.reportValidity()) return;
    setAwaitingCaptcha(true);
    fireGtag("generate_lead", {
      event_category: "trip_booking",
      event_label: selectedPackage?.title || (isCustom ? "custom" : "unknown"),
    });
    const formData = new FormData(e.currentTarget);
    formData.set("packageTitle", selectedPackage?.title ?? "");
    formData.set("travelers", String(travelers));
    formData.set("startDate", toISO(range?.from));
    formData.set("endDate", toISO(range?.to));
    getRecaptchaToken("booking").then((token) => {
      formData.set("captchaToken", token);
      setAwaitingCaptcha(false);
      startTransition(() => action(formData));
    });
  };

  const busy = pending || awaitingCaptcha;

  // On success the request is done — a quiet confirmation replaces the form.
  if (state?.ok) {
    return (
      <div
        role="status"
        className={cn(
          "flex items-start gap-3 rounded-xl border border-emerald-200 bg-emerald-50 p-6",
          className,
        )}
      >
        <Check
          className="mt-0.5 h-5 w-5 shrink-0 text-emerald-600"
          aria-hidden
        />
        <div>
          <p className="text-base font-semibold text-emerald-900">
            Booking request sent
          </p>
          <p className="mt-1 text-sm text-emerald-800">{state.message}</p>
        </div>
      </div>
    );
  }

  const fieldError = (key: string) =>
    serverErr[key] ? (
      <p className="mt-1.5 text-xs text-danger">{serverErr[key]}</p>
    ) : null;

  return (
    <form
      ref={formRef}
      action={action}
      onSubmit={handleSubmit}
      noValidate
      className={cn("relative", className)}
    >
      {busy && (
        <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/60 backdrop-blur-[1px]">
          <span
            className="h-8 w-8 animate-spin rounded-full border-[3px] border-headings/25 border-t-headings"
            aria-hidden
          />
        </div>
      )}

      {/* Hidden / anti-spam + composed values */}
      <input type="hidden" name="ts" value={ts} />
      <input type="hidden" name="url" value={pageUrl} />
      <input type="hidden" name="captchaToken" defaultValue="" />
      <input
        type="hidden"
        name="packageTitle"
        value={selectedPackage?.title ?? ""}
      />
      <input type="hidden" name="startDate" value={toISO(range?.from)} />
      <input type="hidden" name="endDate" value={toISO(range?.to)} />
      <input type="hidden" name="travelers" value={travelers} />
      <input
        type="text"
        name="website"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden
        className="absolute left-[-9999px]"
      />

      <div className="space-y-10">
        {/* ---- 1 · Trip -------------------------------------------------- */}
        <section>
          <StepHeading
            step={1}
            title="Your trip"
            hint="Pick a trip or describe a custom itinerary, then choose your dates."
          />

          <div className="grid md:pl-10 grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label
                htmlFor="packageId"
                className="uppercase tracking-wider font-bold"
              >
                Package
              </Label>
              <Select
                id="packageId"
                name="packageId"
                value={packageId}
                onChange={(e) => setPackageId(e.target.value)}
                error={Boolean(serverErr.packageId)}
              >
                <option value="">Select a package</option>
                <option value="0">Custom / tailor-made trip</option>
                {packages.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.title}
                  </option>
                ))}
              </Select>
              {fieldError("packageId")}
            </div>

            {isCustom ? (
              <div className="sm:col-span-2">
                <Label
                  htmlFor="customTitle"
                  className="uppercase tracking-wider font-bold"
                >
                  Trip details
                </Label>
                <Textarea
                  id="customTitle"
                  name="customTitle"
                  rows={3}
                  placeholder="Destinations, style, must-sees, rough budget…"
                  error={Boolean(serverErr.customTitle)}
                />
                {fieldError("customTitle")}
              </div>
            ) : null}
            <div>
              <Label
                htmlFor="travelers"
                className="uppercase tracking-wider font-bold"
              >
                Travellers
              </Label>

              <Stepper
                value={travelers}
                onChange={setTravelers}
                min={1}
                max={50}
              />
            </div>

            <div className="sm:col-span-2">
              <div className="flex flex-wrap items-end justify-between gap-2">
                <Label
                  htmlFor="tripDatesCalendar"
                  className="uppercase tracking-wider font-bold mb-0"
                >
                  Trip dates
                </Label>
                <span className="text-xs text-muted">
                  {range?.from ? (
                    <>
                      <span className="font-medium text-text">
                        {fmt(range.from)}
                        {range.to ? ` – ${fmt(range.to)}` : ""}
                      </span>
                      {tripNights ? (
                        <span className="ml-2 text-muted-soft">
                          {tripNights} night{tripNights === 1 ? "" : "s"}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    "Select a start and end date"
                  )}
                </span>
              </div>
              <div
                id="tripDatesCalendar"
                className="mt-2 rounded-[var(--radius-inset)] border border-line bg-white p-2 sm:p-3"
              >
                <DayPicker
                  mode="range"
                  selected={range}
                  onSelect={setRange}
                  disabled={{ before: today }}
                  defaultMonth={range?.from ?? today}
                  numberOfMonths={2}
                  className="custom-date-picker"
                />
              </div>
              {fieldError("startDate") || fieldError("endDate")}
            </div>
          </div>
        </section>

        {/* ---- 2 · Lead traveller ------------------------------------------ */}
        <section>
          <StepHeading
            step={2}
            title="Lead traveller"
            hint="Who should we contact about this booking?"
          />

          <div className="grid md:pl-10 grid-cols-1 gap-x-5 gap-y-5 sm:grid-cols-2">
            <div>
              <Label
                htmlFor="fullName"
                className="uppercase tracking-wider font-bold"
              >
                Full name
              </Label>
              <Input
                id="fullName"
                name="fullName"
                autoComplete="name"
                error={Boolean(serverErr.fullName)}
              />
              {fieldError("fullName")}
            </div>

            <div>
              <Label
                htmlFor="email"
                className="uppercase tracking-wider font-bold"
              >
                Email
              </Label>
              <Input
                id="email"
                name="email"
                type="email"
                autoComplete="email"
                error={Boolean(serverErr.email)}
              />
              {fieldError("email")}
            </div>

            <div>
              <Label
                htmlFor="nationality"
                className="uppercase tracking-wider font-bold"
              >
                Nationality
              </Label>
              <Select
                id="nationality"
                name="nationality"
                defaultValue=""
                error={Boolean(serverErr.nationality)}
              >
                <option value="">Select country</option>
                {countryNameOptions.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </Select>
              {fieldError("nationality")}
            </div>

            <div>
              <Label
                htmlFor="dateOfBirth"
                className="uppercase tracking-wider font-bold"
              >
                Date of birth (optional)
              </Label>
              <Input
                id="dateOfBirth"
                name="dateOfBirth"
                type="date"
                max={toISO(today)}
              />

              {fieldError("dateOfBirth")}
            </div>

            <div className="sm:col-span-2">
              <Label
                htmlFor="mobileNumber"
                className="uppercase tracking-wider font-bold"
              >
                Mobile number
              </Label>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-12">
                <div className="sm:col-span-4">
                  <Select
                    name="countryCode"
                    aria-label="Country dialling code"
                    defaultValue=""
                    error={Boolean(serverErr.countryCode)}
                  >
                    <option value="">Code</option>
                    {dialCodeOptions.map((c) => (
                      <option key={c.key} value={c.value}>
                        {c.label}
                      </option>
                    ))}
                  </Select>
                </div>
                <div className="sm:col-span-8">
                  <Input
                    id="mobileNumber"
                    name="mobileNumber"
                    type="tel"
                    autoComplete="tel"
                    placeholder="Phone number"
                    error={Boolean(serverErr.mobileNumber)}
                  />
                </div>
              </div>
              {fieldError("countryCode") || fieldError("mobileNumber")}
            </div>

            <div className="sm:col-span-2">
              <Label
                htmlFor="specialRequirement"
                className="uppercase tracking-wider font-bold"
              >
                Anything we should know?
              </Label>
              <Textarea
                id="specialRequirement"
                name="specialRequirement"
                rows={4}
                placeholder="Diet, fitness, arrival details, special requests…"
                error={Boolean(serverErr.specialRequirement)}
              />
              {fieldError("specialRequirement")}
            </div>
          </div>
        </section>

        {/* ---- 3 · Confirm ---------------------------------------------- */}
        <section className="space-y-4  md:pl-10">
          <label className="flex items-start gap-2.5 text-sm text-text">
            <input
              type="checkbox"
              name="terms"
              checked={terms}
              onChange={() => setTerms((v) => !v)}
              className="mt-0.5 h-4 w-4 rounded border-headings/40 text-primary focus-visible:ring-2 focus-visible:ring-primary/35"
            />
            <span>
              I accept the{" "}
              <Link
                href="/terms-and-conditions"
                target="_blank"
                className="font-medium text-primary hover:underline"
              >
                terms &amp; conditions
              </Link>
              .
            </span>
          </label>
          {fieldError("terms")}

          {state && !state.ok ? (
            <p
              role="alert"
              className="rounded-[var(--radius-inset)] border border-danger/30 bg-danger/5 px-4 py-3 text-sm text-danger"
            >
              {state.message}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            disabled={!terms || busy}
            className="w-full justify-center sm:w-auto"
          >
            {busy ? "Sending…" : "Confirm Booking"}
          </Button>
        </section>
      </div>
    </form>
  );
}
